import { queryOptions, useMutation, useSuspenseQuery } from '@tanstack/react-query';

import type { MutationConfig } from '@/lib/react-query';
import type { LoaderProps } from '@/lib/router';
import { api, extractData } from '@/lib/treaty';

import type { EstateSchemaType } from '../schemas/estate';

export type Estates = Awaited<ReturnType<typeof getEstates>>;

export type EstatesQueryFilters = Parameters<typeof api.estates.get>;

export type CreateEstateType = Parameters<typeof api.estates.post>;

export type Estate = Estates[number];

type CreateEstateOptions = {
  mutationConfig?: MutationConfig<typeof createEstate>;
};

type EditEstateOptions = {
  mutationConfig?: MutationConfig<typeof editEstate>;
};

type DeleteEstateOptions = {
  mutationConfig?: MutationConfig<typeof deleteEstate>;
};

export const getEstates = (...args: EstatesQueryFilters) => {
  return extractData(api.estates.get(...args));
};

export const getEstatesQueryOptions = (...args: EstatesQueryFilters) => {
  return queryOptions({
    queryKey: ['estates', ...args],
    queryFn: () => getEstates(...args),
  });
};

export const ensureEstatesQueryData = ({ context: { queryClient } }: LoaderProps) => {
  return queryClient.ensureQueryData(getEstatesQueryOptions({ query: {} }));
};

export const useEstates = (...args: EstatesQueryFilters) => {
  return useSuspenseQuery({ ...getEstatesQueryOptions(...args) });
};

const createEstate = (...args: CreateEstateType) => {
  return extractData(api.estates.post(...args));
};

export const useCreateEstate = ({ mutationConfig }: CreateEstateOptions = {}) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: createEstate,
  });
};

export const editEstate = ({ id, ...args }: EstateSchemaType & { id: string }) => {
  return extractData(api.estates({ id }).put(args));
};

export const useEditEstate = ({ mutationConfig }: EditEstateOptions = {}) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: editEstate,
  });
};

const deleteEstate = (id: string) => {
  return extractData(api.estates({ id }).delete(id));
};

export const useDeleteEstate = ({ mutationConfig }: DeleteEstateOptions = {}) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: deleteEstate,
  });
};
